<template>
    <transition name="cz3__fade">
        <div
            v-if="showModal"
            class="z-[100] fixed top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-black bg-opacity-30"
        >
            <div class="shadow-md max-h-full overflow-auto w-[296px] lg:w-[500px] py-[32px] px-[18px] bg-white text-ink">
                <div class="mb-[20px] flex items-center justify-between">
                    <div class="text-[16px] font-semibold tracking-wide capitalize">
                        {{ heading }}
                    </div>
                    <close-button
                        @click="emitClose"
                        width="16"
                    />
                </div>

                <div v-if="!emailSent">
                    <div class="mb-[28px] flex flex-col">
                        <div class="flex flex-col mb-[20px]">
                            <base-input-label
                                :is-required="true"
                                label-position="left"
                                :label-text="$t('YOUR_NAME')"
                                class="text-[14px]"
                            />

                            <base-input
                                input-name="senderName"
                                :input-value="senderName"
                                @change="senderName = $event.target.value"
                                :error-message="senderNameError"
                                :show-error-message="senderNameError !== ''"
                                class="w-full"
                            />
                        </div>

                        <div class="flex flex-col mb-[20px]">
                            <base-input-label
                                :is-required="true"
                                label-position="left"
                                :label-text="$t('YOUR_EMAIL')"
                                class="text-[14px]"
                            />

                            <base-input
                                input-type="email"
                                input-name="senderEmail"
                                :input-value="senderEmail"
                                @change="senderEmail = $event.target.value"
                                :error-message="senderEmailError"
                                :show-error-message="senderEmailError !== ''"
                                class="w-full"
                            />
                        </div>

                        <div class="flex flex-col mb-[20px]">
                            <base-input-label
                                :is-required="true"
                                label-position="left"
                                :label-text="$t('RECIPIENT_NAME')"
                                class="text-[14px]"
                            />

                            <base-input
                                input-name="recipientName"
                                :input-value="recipientName"
                                @change="recipientName = $event.target.value"
                                :error-message="recipientNameError"
                                :show-error-message="recipientNameError !== ''"
                                class="w-full"
                            />
                        </div>

                        <div class="flex flex-col mb-[20px]">
                            <base-input-label
                                :is-required="true"
                                label-position="left"
                                :label-text="$t('RECIPIENT_EMAIL')"
                                class="text-[14px]"
                            />

                            <base-input
                                input-type="email"
                                input-name="recipientEmail"
                                :input-value="recipientEmail"
                                @change="recipientEmail = $event.target.value"
                                :error-message="recipientEmailError"
                                :show-error-message="recipientEmailError !== ''"
                                class="w-full"
                            />
                        </div>

                        <div class="flex flex-col mb-[14px]">
                            <base-input-label
                                label-position="left"
                                :label-text="$t('MESSAGE')"
                                class="text-[14px]"
                            />

                            <div class="w-full">
                                <base-input-textarea
                                    class="text-base"
                                    input-name="message"
                                    @input="updateMessage"
                                    :show-error-message="messageError !== ''"
                                    :error-message="messageError"
                                    :input-placeholder="$t('MESSAGE_PLACEHOLDER')"
                                />
                            </div>
                        </div>

                        <div class="w-full">
                            <base-checkbox checkbox-id="sendCopy"
                                @checkboxChecked="copyMeUpdated"
                            >
                                {{ $t('SEND_COPY') }}
                            </base-checkbox>
                        </div>
                    </div>

                    <div class="flex justify-end gap-4">
                        <base-button-flat
                            @click="emitClose"
                            variant="secondary"
                            class="w-36 h-[34px] capitalize"
                        >
                            {{ $t('CANCEL') }}
                        </base-button-flat>

                        <base-button-flat
                            :is-disabled="emailSending"
                            @click="validateFields"
                            class="w-36 h-[34px] capitalize"
                            variant="tertiary"
                        >
                            <loading-spinner
                                v-if="emailSending"
                                class="h-6 w-6 text-white"
                            />

                            <span v-else>
                                {{ $t('SEND') }}
                            </span>
                        </base-button-flat>
                    </div>
                </div>

                <div v-else>
                    <p class="mb-6">
                        {{ $t('EMAIL_SENT') }}
                    </p>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    import allStores from '../stores/all';

    import BaseInputLabel from './BaseInputLabel.vue';
    import BaseInput from './BaseInput.vue';
    import BaseInputTextarea from './BaseInputTextarea.vue';
    import BaseCheckbox from './BaseCheckbox.vue';
    import BaseButtonFlat from './buttons/BaseButtonFlat.vue';
    import CloseButton from './buttons/CloseButton.vue';
    import LoadingSpinner from './LoadingSpinner.vue';

    export default {
        mixins: [allStores],

        components: {
            BaseInputLabel,
            BaseInput,
            BaseInputTextarea,
            BaseCheckbox,
            BaseButtonFlat,
            CloseButton,
            LoadingSpinner,
        },

        props: {
            showModal: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                senderName: '',
                senderEmail: '',
                recipientName: '',
                recipientEmail: '',
                message: '',
                senderNameError: '',
                senderEmailError: '',
                recipientNameError: '',
                recipientEmailError: '',
                messageError: '',
                emailSent: false,
                emailSending: false,
                copyMe: false,
            };
        },

        computed: {
            heading() {
                return this.emailSent ? `${this.$t('EMAIL_SUMMARY')}` : `${this.$t('EMAIL')}`;
            },

            viewController() {
                return this.customizerStore.viewController;
            },
        },

        methods: {
            updateMessage(inputValue) {
                this.message = inputValue;
            },

            copyMeUpdated(value) {
                this.copyMe = value;
            },

            validateFields($event) {
                const allowTest = $event.shiftKey;
                const onlyConsole = $event.shiftKey && $event.altKey;

                this.senderNameError = this.senderName ? '' : `${this.$t('REQUIRED')}`;
                this.senderEmailError = this.senderEmail ? this.validateEmail(this.senderEmail) : `${this.$t('REQUIRED')}`;
                this.recipientNameError = this.recipientName ? '' : `${this.$t('REQUIRED')}`;
                this.recipientEmailError = this.recipientEmail ? this.validateEmail(this.recipientEmail) : `${this.$t('REQUIRED')}`;

                this.checkMessageForProfanity().then(() => {
                    if (!this.senderNameError && !this.senderEmailError && !this.recipientNameError && !this.recipientEmailError && !this.messageError) {
                        this.submitEmail(allowTest, onlyConsole);
                    }
                });
            },

            validateEmail(emailAddress) {
                let errorMessage = `${this.$t('EMAIL_INVALID')}`;

                // eslint-disable-next-line
                if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(emailAddress)) {
                    errorMessage = '';
                }

                return errorMessage;
            },

            checkMessageForProfanity() {
                return new Promise((resolve) => {
                    if ((this.message ?? '').length > 0) {
                        this.viewController.reviewText(this.message).then((result) => {
                            if (result.hasProfanity) {
                                this.messageError = `${this.$t('INVALID_ERROR')}`;
                            } else {
                                this.messageError = '';
                            }

                            resolve();
                        }).catch(() => {
                            this.messageError = `${this.$t('INVALID_ERROR')}`;

                            resolve();
                        });
                    } else {
                        this.messageError = '';

                        resolve();
                    }
                });
            },

            submitEmail(allowTest, onlyConsole) {
                this.emailSending = true;

                const target = this.viewController.blueprint.custom['v-share-target'];
                const key = this.viewController.blueprint.custom['v-share-key'];

                const cart = this.customizerStore.formatDescriptionData(this.viewController);

                this.customizerStore.saveRecipe().then((recipe) => {
                    cart.recipe = recipe.id;

                    const payload = {
                        locale: this.customizerStore.options.locale,
                        fromEmail: this.senderEmail,
                        fromName: this.senderName,
                        toEmail: this.recipientEmail,
                        toName: this.recipientName,
                        message: this.message,
                        copyMe: this.copyMe,
                        cart,
                    };

                    if (onlyConsole) {
                        // eslint-disable-next-line
                        console.log('Email payload: ', payload);
                        // eslint-disable-next-line
                        console.log('Would send using: ', target);

                        this.emailSending = false;
                        this.emailSent = true;
                    } else if (allowTest && this.customizerStore.options.onShareEmailTest) {
                        this.customizerStore.options.onShareEmailTest(payload);
                        this.appStore.globalDone();
                    } else {
                        fetch(`${target}?code=${key}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(payload),
                        })
                            .then((response) => {
                                if (!response.ok) {
                                    this.emailSending = false;
                                    this.messageError = this.$t('SEND_ERROR');
                                }

                                return response.json();
                            })
                            .then(() => {
                                this.emailSending = false;
                                this.emailSent = true;
                            })
                            .catch(() => {
                                this.emailSending = false;
                                this.messageError = this.$t('SEND_ERROR');
                            });
                    }
                });
            },

            reset() {
                this.senderName = '';
                this.senderEmail = '';
                this.recipientName = '';
                this.recipientEmail = '';
                this.message = '';
                this.senderNameError = '';
                this.senderEmailError = '';
                this.recipientNameError = '';
                this.recipientEmailError = '';
                this.messageError = '';
                this.emailSent = false;
                this.emailSending = false;

                this.emitClose();
            },

            emitClose() {
                this.$emit('modalClose');

                if (this.emailSent) {
                    this.reset();
                }
            },
        },
    };
</script>
